import type { Reminder, Service } from '@/core/domain';
import type { ReminderRepository } from '@/features/app/reminders/domain/repositories/reminder-repository';
import type { ServiceRepository } from '../repositories/service-repository';

/** Compara só a parte data (YYYY-MM-DD) para detectar mudança de manutenção prevista. */
function maintenanceDateKey(iso: string): string {
  return iso.trim().slice(0, 10);
}

/**
 * Ao criar serviço, o lembrete usa o mesmo `id` do serviço (`CreateServiceUseCase`).
 * Ao editar datas/período/equipamento/cliente no serviço, o lembrete correspondente precisa refletir isso.
 */
export class UpdateServiceUseCase {
  constructor(
    private readonly serviceRepository: ServiceRepository,
    private readonly reminderRepository: ReminderRepository
  ) {}

  async execute(userId: string, id: string, service: Partial<Service>): Promise<Service> {
    const updated = await this.serviceRepository.update(userId, id, service);
    await this.syncReminderFromService(id, updated);
    return updated;
  }

  private async syncReminderFromService(serviceId: string, service: Service): Promise<void> {
    const reminder = await this.reminderRepository.getById(serviceId);
    if (!reminder) return;

    const now = new Date().toISOString();
    const newExpected = service.next_maintenance_date;
    const expectedChanged =
      maintenanceDateKey(reminder.expected_date) !== maintenanceDateKey(newExpected);

    const patch: Partial<Reminder> = {
      expected_date: newExpected,
      equipment_reference: service.equipment_reference,
      client_id: service.client_id,
      updated_at: now,
    };

    if (expectedChanged) {
      patch.sent_at = [];
      patch.send_failures = [];
      patch.status = 'scheduled';
    }

    await this.reminderRepository.update(serviceId, patch);
  }
}
